import { FormattedMessage } from "react-intl";
import ColorLink from "../common/links/colorLink";
import { ElementType } from "react";
import { usePage } from "@inertiajs/react";
import { formatDate } from "@/utils/dates";

interface ConditionalWrapperInterface {
    condition: boolean;
    Wrapper: ElementType;
    children: React.ReactNode;
}

const ConditionalWrapper = ({
    condition,
    Wrapper,
    children,
}: ConditionalWrapperInterface) => {
    if (condition) {
        return <Wrapper>{children}</Wrapper>;
    }
    return children;
};

interface HostDetailsInterface {
    link?: string;
    image?: string;
    name: string;
    initials: string;
    createdAt: string;
    approvedOpportunityCount: number | string;
    approvedGroupCount: number | string;
    description: string;
}

export default function HostDetails({
    link,
    image,
    name,
    initials,
    createdAt,
    approvedOpportunityCount,
    approvedGroupCount,
    description,
}: HostDetailsInterface) {
    const { realm } = usePage().props;
    const created = formatDate(createdAt, realm, {
        month: "short",
        year: "numeric",
    });
    return (
        <div className="mt-6 pb-6 sm:mt-12 sm:pb-12 space-y-4 border-b-gray-300 border-b">
            <div className="flex space-x-4">
                <div className="flex sm:items-center space-x-4">
                    <ConditionalWrapper
                        condition={!!link}
                        Wrapper={({ children }) => (
                            <a href={link}>{children}</a>
                        )}
                    >
                        <div
                            className={`flex items-center justify-center rounded-md ${
                                image ? "bg-white" : "bg-voice"
                            } border border-gray-300 size-16 sm:size-24 p-2`}
                        >
                            {image ? (
                                <img src={image} alt={`Logo for ${name}`} />
                            ) : (
                                <span className="text-lg text-white font-semibold">
                                    {initials}
                                </span>
                            )}
                        </div>
                    </ConditionalWrapper>
                    <div>
                        <h2 className="text-xl sm:text-2xl font-medium text-gray-900">
                            {name}
                        </h2>
                        <p className="text-lg font-light text-gray-500">
                            <FormattedMessage id="Joined in" /> {created}
                        </p>
                        <div className="flex items-center text-sm text-gray-500">
                            <div className="mr-2">
                                <span className="font-semibold mr-1">
                                    {approvedOpportunityCount}
                                </span>
                                <FormattedMessage id="Opportunities" />
                            </div>
                            {approvedGroupCount && (
                                <div>
                                    <span className="font-semibold mr-1">
                                        {approvedGroupCount}
                                    </span>
                                    <FormattedMessage id="Groups" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {link && (
                    <div className="hidden sm:block sm:grow">
                        <div className="grid justify-items-end">
                            <ColorLink
                                color="white"
                                href={link}
                                inertia={false}
                            >
                                <FormattedMessage id="View profile" />
                            </ColorLink>
                        </div>
                    </div>
                )}
            </div>
            <div className="mt-5 text-gray-900 leading-6 font-light">
                <p
                    dangerouslySetInnerHTML={{
                        __html: description,
                    }}
                />
                <div className="block mt-2 sm:hidden">
                    <a
                        href={link}
                        className="font-medium underline underline-offset-4"
                    >
                        <FormattedMessage id="View profile" />
                    </a>
                </div>
            </div>
        </div>
    );
}
